import axios from '@axios'

/**
 * ========================================
 * Check-in Vuex Store Module
 * ========================================
 *
 * PURPOSE:
 * This module manages the check-in system where users earn rewards by making
 * consecutive daily deposits of 300+ baht for 3 days in a row.
 *
 * BUSINESS LOGIC:
 * - User must deposit >= 300 baht per day
 * - Must be consecutive days (no gaps allowed)
 * - After 3 consecutive days, user can claim 100 baht reward
 * - If user misses a day, streak resets to 0
 * - After claiming reward, streak resets and can start again
 *
 * BACKEND INTEGRATION:
 * The backend should:
 * 1. Auto-detect when user makes a qualifying deposit (>= 300 baht)
 * 2. Track consecutive days using server timezone (Asia/Bangkok)
 * 3. Reset streak if user misses a day (24+ hours since last deposit)
 * 4. Store check-in history for display purposes
 * 5. Validate claim requests (only allow when currentStreak === 3)
 * 6. Add credit to user account on successful claim
 *
 * API ENDPOINTS NEEDED:
 * - GET  /member/checkin/status - Fetch current check-in status
 * - POST /member/checkin/claim  - Claim the 100 baht reward
 *
 * ========================================
 * 🔧 CONFIGURATION - EASY API SWAP
 * ========================================
 * Change USE_MOCK_DATA to switch between mock and real API
 */

// ⚙️ CONFIG: Set to true to use mock data for development
const USE_MOCK_DATA = false

// 📦 MOCK DATA: Simulates API responses for development
const MOCK_RESPONSES = {
  // Mock response for GET /member/checkin/status
  fetchStatus: {
    currentStreak: 2, // Example: User has checked in 2 consecutive days
    checkInHistory: [
      {
        date: '2026-01-27',
        amount: 500,
        qualified: true
      },
      {
        date: '2026-01-18',
        amount: 350,
        qualified: true
      },
      {
        date: '2026-01-25',
        amount: 200,
        qualified: false // Below 300 baht minimum
      },
      {
        date: '2026-01-24',
        amount: 1000,
        qualified: true
      }
    ],
    canClaim: false, // Will be true when currentStreak === 3
    lastCheckInDate: '2026-01-27',
    rewardAmount: 300
  },

  // Mock response for POST /member/checkin/claim
  claimReward: {
    success: true,
    creditAdded: 300,
    newBalance: 1234.56,
    message: 'รับรางวัลสำเร็จ'
  }
}

export default {
  namespaced: true,

  /**
   * State
   * - currentStreak: Number of consecutive days with qualifying deposits (0-3)
   * - checkInHistory: Array of recent check-in attempts with deposit amounts
   * - canClaim: Boolean flag indicating if user can claim the 100 baht reward
   * - loading: Loading state for fetching check-in status
   * - claiming: Loading state for claiming reward
   * - rewardAmount: The reward amount in baht (default 100)
   */
  state: {
    currentStreak: 0,
    checkInHistory: [],
    canClaim: false,
    loading: false,
    lastFetched: null,
    claiming: false,
    rewardAmount: 300
  },
  /**
   * Getters
   * These computed properties are used in the CheckInPage component
   */
  getters: {
    // Progress percentage (0%, 33%, 66%, 100%)
    progressPercent: state => (state.currentStreak / 3) * 100,

    // Days remaining until reward (3, 2, 1, or 0)
    daysRemaining: state => Math.max(0, 3 - state.currentStreak),

    // True when user has completed 3 consecutive days
    isCompleted: state => state.currentStreak === 3,

    // Loading state for status fetch
    isLoading: state => state.loading,

    // Loading state for claim action
    isClaiming: state => state.claiming
  },
  mutations: {
    SET_STATUS(state, payload) {
      state.currentStreak = payload.currentStreak || 0
      state.checkInHistory = payload.checkInHistory || []
      state.canClaim = payload.canClaim || false
      state.rewardAmount = payload.rewardAmount || 300
      state.lastFetched = Date.now()
    },
    SET_LOADING(state, value) {
      state.loading = value
    },
    SET_CLAIMING(state, value) {
      state.claiming = value
    },
    RESET_STREAK(state) {
      state.currentStreak = 0
      state.canClaim = false
      state.checkInHistory = []
    },
    INCREMENT_STREAK(state, depositData) {
      state.currentStreak = Math.min(state.currentStreak + 1, 3)
      if (depositData) {
        state.checkInHistory.unshift(depositData)
      }
      state.canClaim = state.currentStreak === 3
    }
  },
  actions: {
    /**
     * Fetch Check-in Status
     *
     * API Endpoint: GET /member/checkin/status
     *
     * Expected Response Format:
     * {
     *   currentStreak: 1,              // Number: 0-3, current consecutive days
     *   checkInHistory: [              // Array: Recent check-in attempts
     *     {
     *       date: '2026-01-27',        // String: Date in YYYY-MM-DD format
     *       amount: 500,               // Number: Deposit amount in baht
     *       qualified: true            // Boolean: true if amount >= 300
     *     },
     *     {
     *       date: '2026-01-26',
     *       amount: 100,
     *       qualified: false           // Did not meet 300 baht minimum
     *     }
     *   ],
     *   canClaim: false,               // Boolean: true when currentStreak === 3
     *   lastCheckInDate: '2026-01-27', // String: Last qualifying check-in date
     *   rewardAmount: 100              // Number: Reward amount in baht
     * }
     */
    async fetchStatus({ commit, state }, { force = false } = {}) {
      // Cache: Skip if recently fetched (within 30 seconds) unless forced
      if (
        !force &&
        state.lastFetched &&
        Date.now() - state.lastFetched < 30000
      ) {
        return state
      }

      commit('SET_LOADING', true)
      try {
        // 🔀 EASY SWAP: Check if using mock data or real API
        if (USE_MOCK_DATA) {
          console.log('🎭 [Check-in] Using MOCK data (fetchStatus)')

          // Simulate API delay for realistic testing (500ms)
          await new Promise(resolve => setTimeout(resolve, 500))

          // Return mock data
          commit('SET_STATUS', MOCK_RESPONSES.fetchStatus)
          return MOCK_RESPONSES.fetchStatus
        }

        // Real API call
        const resp = await axios.get('/member/checkin/status')
        commit('SET_STATUS', resp.data)
        return resp.data
      } catch (error) {
        console.error('Error fetching check-in status:', error)

        // Fallback to mock data if API fails
        commit('SET_STATUS', MOCK_RESPONSES.fetchStatus)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Claim Reward
     *
     * API Endpoint: POST /member/checkin/claim
     *
     * Request Body: None (user identification from session/token)
     *
     * Expected Response Format:
     * {
     *   success: true,              // Boolean: Claim success status
     *   creditAdded: 100,           // Number: Amount of credit added
     *   newBalance: 1234.56,        // Number: User's new credit balance
     *   message: 'รับรางวัลสำเร็จ'    // String: Success message (optional)
     * }
     *
     * Error Response (if user doesn't meet requirements):
     * {
     *   success: false,
     *   error: 'NOT_QUALIFIED',
     *   message: 'ไม่ครบเงื่อนไขการรับรางวัล'
     * }
     */
    async claimReward({ commit, dispatch }) {
      commit('SET_CLAIMING', true)
      try {
        // 🔀 EASY SWAP: Check if using mock data or real API
        if (USE_MOCK_DATA) {
          console.log('🎭 [Check-in] Using MOCK data (claimReward)')

          // Simulate API delay for realistic testing (800ms)
          await new Promise(resolve => setTimeout(resolve, 800))

          // Reset streak after successful claim
          commit('RESET_STREAK')

          // Refresh credit balance in member store to show updated balance
          await dispatch('member/fetchCredit', { force: true }, { root: true })

          // Return mock response
          return MOCK_RESPONSES.claimReward
        }

        // Real API call
        const resp = await axios.post('/member/checkin/claim')

        // Reset streak after successful claim
        commit('RESET_STREAK')

        // Refresh credit balance in member store to show updated balance
        await dispatch('member/fetchCredit', { force: true }, { root: true })

        return resp.data
      } catch (error) {
        console.error('Error claiming reward:', error)
        throw error
      } finally {
        commit('SET_CLAIMING', false)
      }
    },

    /**
     * Manual Check-in Trigger (Development/Testing Only)
     *
     * This action is for manual testing. In production, the backend should
     * automatically detect qualifying deposits and update the check-in status.
     *
     * When a user makes a deposit >= 300 baht, the backend should:
     * 1. Check if it's a consecutive day from their last check-in
     * 2. Increment their streak if consecutive
     * 3. Reset streak to 1 if not consecutive
     * 4. Add the deposit to their check-in history
     *
     * @param {Number} depositAmount - The deposit amount to test with
     */
    async checkIn({ commit, dispatch }, depositAmount) {
      try {
        const depositData = {
          date: new Date().toISOString().split('T')[0],
          amount: depositAmount,
          qualified: depositAmount >= 300
        }

        if (depositData.qualified) {
          commit('INCREMENT_STREAK', depositData)
        }

        // Refresh status from server to sync with backend
        await dispatch('fetchStatus', { force: true })

        return depositData
      } catch (error) {
        console.error('Error during check-in:', error)
        throw error
      }
    },

    /**
     * Reset Streak (Admin/Testing)
     * Manually reset the check-in streak to 0
     */
    resetStreak({ commit }) {
      commit('RESET_STREAK')
    }
  }
}

/**
 * ========================================
 * 🔧 HOW TO SWITCH TO REAL API
 * ========================================
 *
 * When backend is ready, simply change ONE line at the top of this file:
 *
 * FROM:  const USE_MOCK_DATA = true
 * TO:    const USE_MOCK_DATA = false
 *
 * That's it! The entire app will automatically use real API calls.
 *
 * ========================================
 * USAGE EXAMPLES
 * ========================================
 *
 * In Vue Components:
 *
 * // 1. Import Vuex helpers
 * import { mapState, mapGetters, mapActions } from 'vuex'
 *
 * // 2. Map state and getters
 * computed: {
 *   ...mapState('checkin', ['currentStreak', 'checkInHistory', 'canClaim']),
 *   ...mapGetters('checkin', ['progressPercent', 'daysRemaining', 'isCompleted'])
 * }
 *
 * // 3. Map actions
 * methods: {
 *   ...mapActions('checkin', ['fetchStatus', 'claimReward'])
 * }
 *
 * // 4. Use in component lifecycle
 * created() {
 *   // Fetch check-in status when component loads
 *   this.fetchStatus()
 * }
 *
 * // 5. Handle claim button click
 * async handleClaim() {
 *   try {
 *     const result = await this.claimReward()
 *     this.$toast.success('รับรางวัลสำเร็จ!')
 *   } catch (error) {
 *     this.$toast.error('เกิดข้อผิดพลาด')
 *   }
 * }
 *
 * ========================================
 * TESTING SCENARIOS
 * ========================================
 *
 * Scenario 1: Day 0 (New User)
 * - currentStreak: 0
 * - progressPercent: 0%
 * - daysRemaining: 3
 * - canClaim: false
 *
 * Scenario 2: Day 1 (First Deposit)
 * - currentStreak: 1
 * - progressPercent: 33%
 * - daysRemaining: 2
 * - canClaim: false
 *
 * Scenario 3: Day 2 (Second Consecutive Day)
 * - currentStreak: 2
 * - progressPercent: 66%
 * - daysRemaining: 1
 * - canClaim: false
 *
 * Scenario 4: Day 3 (Third Consecutive Day - Ready to Claim)
 * - currentStreak: 3
 * - progressPercent: 100%
 * - daysRemaining: 0
 * - canClaim: true
 * - Show pulsing claim button
 *
 * Scenario 5: After Claiming Reward
 * - currentStreak: 0 (resets)
 * - progressPercent: 0%
 * - daysRemaining: 3
 * - canClaim: false
 * - User can start new cycle
 *
 * Scenario 6: Missed a Day (Streak Broken)
 * - If user deposits on Day 1, skips Day 2, deposits on Day 3
 * - Backend should reset streak to 1 (not 2)
 * - currentStreak: 1
 * - User must start over from Day 1
 */
